import React, { useState, useEffect } from 'react';
import { Sparkles, BarChart3, Database, Bell, ArrowRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getTerm, useTerms } from '@/lib/branding';

const STEPS = [
  { target: 'nav-ai-copilot', termKey: 'ai_explore', title: 'AI Explore', desc: 'Ask questions about your data in plain English. AI analyzes trusted models and returns charts, tables, and insights.', icon: Sparkles, position: 'right' },
  { target: 'nav-dashboards', termKey: 'insights', title: 'Insights', desc: 'Build interactive dashboards and paginated reports. Drag fields, add charts, KPIs, and free-positioned visuals.', icon: BarChart3, position: 'right' },
  { target: 'nav-group-data', termKey: 'data', title: 'Data', desc: 'Connect data sources, prepare pipelines, build semantic models, and define your business glossary.', icon: Database, position: 'right' },
  { target: 'notifications-btn', title: 'Notifications', desc: 'Stay updated with alerts, scheduled report deliveries, and team activity — all in one place.', icon: Bell, position: 'bottom' },
  { target: 'global-search', title: 'Search', desc: 'Find any insight, model, metric, or glossary term instantly from the global search bar.', icon: Sparkles, position: 'bottom' },
];

export function OnboardingTour({ onComplete }) {
  useTerms();
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const current = STEPS[step];

  useEffect(() => {
    const el = document.querySelector(`[data-testid="${current.target}"]`);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);
    }
  }, [step, current]);

  const next = () => { if (step < STEPS.length - 1) setStep(step + 1); else onComplete(); };
  const Icon = current.icon;

  // Calculate tooltip position clamped to viewport
  let tooltipStyle = {};
  if (targetRect) {
    let top, left;
    if (current.position === 'right') {
      top = targetRect.top + targetRect.height / 2 - 50;
      left = targetRect.right + 16;
    } else {
      top = targetRect.bottom + 12;
      left = targetRect.left;
    }
    // Clamp to viewport
    top = Math.max(12, Math.min(top, window.innerHeight - 200));
    left = Math.max(12, Math.min(left, window.innerWidth - 340));
    tooltipStyle = { top, left };
  }

  return (
    <>
      {/* Overlay with cutout for highlighted element */}
      <svg className="fixed inset-0 z-[150] pointer-events-none" width="100%" height="100%" style={{ pointerEvents: 'none' }}>
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect x={targetRect.left - 6} y={targetRect.top - 6} width={targetRect.width + 12} height={targetRect.height + 12} rx="12" fill="black" />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.5)" mask="url(#tour-mask)" />
      </svg>
      {/* Highlight border around target */}
      {targetRect && (
        <div className="fixed z-[151] pointer-events-none rounded-xl border-2 border-primary shadow-[0_0_20px_rgba(59,130,246,0.3)]" style={{ top: targetRect.top - 6, left: targetRect.left - 6, width: targetRect.width + 12, height: targetRect.height + 12 }} />
      )}
      {/* Click blocker (allows clicking Next/Skip but blocks everything else) */}
      {/* Backdrop — non-blocking (pointer-events:none) so users can
          keep interacting with the app; the tour has explicit Skip / X
          / Next controls on the tooltip. Previously this backdrop
          intercepted clicks on interactive elements underneath. */}
      <div className="fixed inset-0 z-[152] pointer-events-none" />
      {/* Tooltip */}
      <div className="fixed z-[153] w-80 glass-card rounded-2xl p-5 border border-primary/30 shadow-2xl" style={tooltipStyle} onClick={e => e.stopPropagation()} data-testid="onboarding-tooltip">
        <div className="flex items-start gap-3 mb-3">
          <div className="p-2 rounded-xl bg-primary/10 border border-primary/20"><Icon className="w-5 h-5 text-primary" /></div>
          <div className="flex-1">
            <h3 className="text-sm font-heading font-semibold text-foreground">{current.termKey ? getTerm(current.termKey) : current.title}</h3>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{current.desc}</p>
          </div>
          <button onClick={onComplete} className="text-muted-foreground/70 hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {STEPS.map((_, i) => <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === step ? 'bg-primary' : i < step ? 'bg-primary/40' : 'bg-muted-foreground/20'}`} />)}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" className="text-xs h-7 rounded-lg" onClick={onComplete}>Skip</Button>
            <Button size="sm" className="text-xs h-7 rounded-lg gap-1" onClick={next}>
              {step < STEPS.length - 1 ? <>Next <ArrowRight className="w-3 h-3" /></> : 'Done'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
